import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tour, TourStep } from '@/types/tours';
import { getTargetsForPage } from '@/services/tourTargetExtractor';
import { cn } from '@/lib/utils';

interface TourFormProps {
  tour: Tour | null;
  pages: { value: string; label: string }[];
  onSave: (data: Partial<Tour>) => void;
  onCancel: () => void;
  loading: boolean;
}

const TourForm: React.FC<TourFormProps> = ({ tour, pages, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState<Partial<Tour>>({
    name: '',
    description: '',
    page: '/chat-juridico',
    steps: [],
    is_active: true,
    auto_start: true,
    show_progress: true,
    continuous: true,
    show_skip_button: true,
  });

  const [availableTargets, setAvailableTargets] = useState<string[]>([]);
  const [openTargetCombobox, setOpenTargetCombobox] = useState<number | null>(null);

  useEffect(() => {
    if (tour) {
      setFormData(tour);
    }
  }, [tour]);

  useEffect(() => {
    if (formData.page) {
      setAvailableTargets(getTargetsForPage(formData.page));
    }
  }, [formData.page]);

  const handleAddStep = () => {
    const newStep: TourStep = {
      target: '',
      title: '',
      content: '',
      placement: 'bottom',
      disableBeacon: false,
    };
    setFormData(prev => ({
      ...prev,
      steps: [...(prev.steps || []), newStep],
    }));
  };

  const handleUpdateStep = (index: number, field: keyof TourStep, value: any) => {
    setFormData(prev => {
      const steps = [...(prev.steps || [])];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, steps };
    });
  };

  const handleRemoveStep = (index: number) => {
    setFormData(prev => ({
      ...prev,
      steps: (prev.steps || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informações Básicas */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Nome do Tour</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Introdução ao Chat Jurídico"
            required
          />
        </div>

        <div>
          <Label htmlFor="description">Descrição</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Breve descrição do que o usuário aprenderá..."
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="page">Página</Label>
          <Select
            value={formData.page}
            onValueChange={(value) => setFormData(prev => ({ ...prev, page: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a página" />
            </SelectTrigger>
            <SelectContent>
              {pages.map(page => (
                <SelectItem key={page.value} value={page.value}>
                  {page.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Configurações */}
      <div className="space-y-3 border-t pt-4">
        <h3 className="font-semibold">Configurações</h3>
        
        <div className="flex items-center justify-between">
          <Label htmlFor="is_active">Tour Ativo</Label>
          <Switch
            id="is_active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="auto_start">Iniciar Automaticamente</Label>
          <Switch
            id="auto_start"
            checked={formData.auto_start}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_start: checked }))}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="show_progress">Mostrar Progresso</Label>
          <Switch
            id="show_progress"
            checked={formData.show_progress}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_progress: checked }))}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="continuous">Modo Contínuo</Label>
          <Switch
            id="continuous"
            checked={formData.continuous}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, continuous: checked }))}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="show_skip_button">Botão Pular</Label>
          <Switch
            id="show_skip_button"
            checked={formData.show_skip_button}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_skip_button: checked }))}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3 border-t pt-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Passos do Tour</h3>
          <Button type="button" onClick={handleAddStep} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Passo
          </Button>
        </div>

        <div className="space-y-4">
          {(formData.steps || []).map((step, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Passo {index + 1}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveStep(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Seletor CSS (target)</Label>
                  <Popover 
                    open={openTargetCombobox === index} 
                    onOpenChange={(open) => setOpenTargetCombobox(open ? index : null)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-mono text-xs h-10"
                      >
                        <span className="truncate">
                          {step.target || 'Selecione ou digite...'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar ou digitar seletor CSS..." 
                          value={step.target}
                          onValueChange={(value) => handleUpdateStep(index, 'target', value)}
                        />
                        <CommandEmpty>
                          <div className="py-6 text-center text-sm">
                            <p className="text-muted-foreground mb-2">
                              Nenhum target encontrado.
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Digite manualmente: [data-tour="id"], .classe, #id
                            </p>
                          </div>
                        </CommandEmpty>
                        <CommandGroup className="max-h-[200px] overflow-auto">
                          {availableTargets.map((target) => (
                            <CommandItem
                              key={target}
                              value={target}
                              onSelect={() => {
                                handleUpdateStep(index, 'target', target);
                                setOpenTargetCombobox(null);
                              }}
                              className="cursor-pointer"
                            >
                              <Check 
                                className={cn(
                                  "mr-2 h-4 w-4", 
                                  step.target === target ? "opacity-100" : "opacity-0"
                                )} 
                              />
                              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                {target}
                              </code>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione da lista ou digite manualmente
                  </p>
                </div>

                <div>
                  <Label>Posição</Label>
                  <Select
                    value={step.placement || 'bottom'}
                    onValueChange={(value) => handleUpdateStep(index, 'placement', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top">Topo</SelectItem>
                      <SelectItem value="bottom">Baixo</SelectItem>
                      <SelectItem value="left">Esquerda</SelectItem>
                      <SelectItem value="right">Direita</SelectItem>
                      <SelectItem value="center">Centro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Título</Label>
                <Input
                  value={step.title}
                  onChange={(e) => handleUpdateStep(index, 'title', e.target.value)}
                  placeholder="Título do passo"
                  required
                />
              </div>

              <div>
                <Label>Conteúdo</Label>
                <Textarea
                  value={step.content}
                  onChange={(e) => handleUpdateStep(index, 'content', e.target.value)}
                  placeholder="Descrição do que o usuário deve fazer ou entender..."
                  rows={3}
                  required
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={step.disableBeacon || false}
                    onCheckedChange={(checked) => handleUpdateStep(index, 'disableBeacon', checked)}
                  />
                  <Label className="text-sm">Desabilitar beacon</Label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : tour ? 'Atualizar Tour' : 'Criar Tour'}
        </Button>
      </div>
    </form>
  );
};

export default TourForm;
